import * as $ from 'jquery';
import { compositeSubscription } from 'common/barrels/core';
import { Observable, BehaviorSubject, Subscription } from 'common/barrels/rx';
import { and } from 'common/functions/observable';

import 'common/styles/rxui/core.less';

export function groupBlur($group: JQuery) {
    return from<FocusEvent>($group, 'focusout')
        .flatMap(() => from<FocusEvent>($(document), 'focusin').first())
        .filter(() => {
            return $group.index(document.activeElement) === -1;
        });
}

export function focus($element: Element): Observable<FocusEvent>;
export function focus($element: JQuery): Observable<FocusEvent>;
export function focus($element: any): Observable<FocusEvent> {
    return Observable.fromEvent<FocusEvent>($element, 'focus');
}

export function blur($element: Element): Observable<FocusEvent>;
export function blur($element: JQuery): Observable<FocusEvent>;
export function blur($element: any): Observable<FocusEvent> {
    return Observable.fromEvent<FocusEvent>($element, 'blur');
}

export function focusin($element: Element): Observable<FocusEvent>;
export function focusin($element: JQuery): Observable<FocusEvent>;
export function focusin($element: any): Observable<FocusEvent> {
    return Observable.fromEvent<FocusEvent>($element, 'focusin');
}

export function input($element: Element): Observable<Event>;
export function input($element: EventTarget): Observable<Event>;
export function input($element: JQuery): Observable<Event>;
export function input($element: any): Observable<Event> {
    return Observable.fromEvent<Event>($element, 'input');
}

export function focusout($element: Element): Observable<FocusEvent>;
export function focusout($element: JQuery): Observable<FocusEvent>;
export function focusout($element: any): Observable<FocusEvent> {
    return Observable.fromEvent<FocusEvent>($element, 'focusout');
}

export function from<T>($element: Element, events: string): Observable<T>;
export function from<T>($element: EventTarget, events: string): Observable<T>;
export function from<T>($element: JQuery, events: string): Observable<T>;
export function from($element: any, events: string): Observable<any> {
    return Observable.fromEvent<any>($element, events);
}

export function on<T>($element: JQuery, events: string): Observable<T>;
export function on($element: any, events: string) {
    //return $element.onAsObservable(events).map((value: any) => value.additionalArguments[0]);
    return Observable.fromEvent($element, events, function () {return (<any>arguments)[1]; });
}

export function click($element: Node): Observable<MouseEvent>;
export function click($element: JQuery): Observable<MouseEvent>;
export function click($element: any): Observable<MouseEvent> {
    return Observable.fromEvent<MouseEvent>($element, 'click');
}

export function dblclick($element: any): Observable<MouseEvent> {
    return Observable.fromEvent<MouseEvent>($element, 'dblclick');
}

export function clickOutside($inside: JQuery) {
    return click(document).map(x => (<any>x).target).filter(x => !$inside.is(x) && !($inside.has(x).length > 0));
}

export function mousedownOutside($inside: JQuery) {
    return mousedown($(document)).map(x => (<any>x).target).filter(x => !$inside.is(x) && !($inside.has(x).length > 0));
}

export function mousedown($element: JQuery) {
    return Observable.fromEvent<MouseEvent>($element, 'mousedown');
}

export function mousedownleft($element: JQuery) {
    return mousedown($element).filter(x => x.which === 1);
}

export function mouseup($element: JQuery) {
    return Observable.fromEvent<MouseEvent>($element, 'mouseup');
}

export function mouseupleft($element: JQuery) {
    return mouseup($element).filter(x => x.which === 1);
}

export function mousemove($element: JQuery) {
    return Observable.fromEvent<MouseEvent>($element, 'mousemove');
}

export function filteredKeys($element: JQuery, keyCodes: number[], predicate?: (event: KeyboardEvent) => boolean) {
    return Observable.fromEvent<KeyboardEvent>($element, 'keydown').filter(x => $.inArray(x.keyCode, keyCodes) !== -1 && (!predicate || predicate(x)));
}

export function key($element: JQuery, preventDefaultWhen?: (e: KeyboardEvent) => boolean) {
    return Observable.fromEvent<KeyboardEvent>($element, 'keydown')
        .let(x => preventDefaultWhen ? x.do(e => { if (preventDefaultWhen(e)) e.preventDefault(); }) : x)
        .map(x => x.keyCode);
}

export function keyEvent($element: JQuery) {
    return Observable.fromEvent<KeyboardEvent>($element, 'keydown');
}

export function change($element: JQuery): Observable<Event>;
export function change($element: any): Observable<Event> {
    return Observable.fromEvent<Event>($element, 'change');
}

export function error($element: JQuery) {
    return Observable.fromEvent($element, 'error').map(x => x);
}

export function scrolled($element: JQuery): Observable<number> {
    return Observable
        .from([$element.scrollLeft()])
        .concat(from($element, 'scroll').map(() => $element.scrollLeft()))
        .distinctUntilChanged();
}

export function valueChanged($input: JQuery, alternativeTrigger?: Observable<any>): Observable<any> {
    return focusin($input)
        .map(() => $input.val())
        .flatMap(x => (alternativeTrigger || focusout($input)).take(1).map(() => ({ oldValue: x, newValue: $input.val() })))
        .filter(x => x.oldValue !== x.newValue).map(x => x.newValue);
}

export function validatedValueChanged($input: JQuery, validator: (x) => boolean): Observable<any> {
    return focusin($input)
        .map(() => $input.val())
        .flatMap(x => focusout($input).take(1).map(() => ({ oldValue: x, newValue: $input.val() })))
        .filter(x => x.oldValue !== x.newValue)
        .map(x => validator(x.newValue) ? x.newValue : x.oldValue);
}

export function hover($element: JQuery) {
    var enter = Observable.fromEvent($element, 'mouseenter').map(() => true);
    var leave = Observable.fromEvent($element, 'mouseleave').map(() => false);

    return enter.merge(leave);
}

export function redrawRequired(driver: Observable<any>, active: Observable<boolean>, visible: Observable<boolean>, resize?: Observable<any>): Observable<{}> {
    return driver.let(x => resize ? x.merge(resize) : x).switchMap(x => and(active, visible).filter(y => y).first());
}

export function windowResized() {
    return from($(window), 'resize');
}

export function enableDragAndDrop($element: JQuery): Observable<DragEvent> {
    const h = e => {
        if (e.originalEvent && e.originalEvent.dataTransfer)
            e.originalEvent.dataTransfer.dropEffect = 'copy';

        e.stopPropagation();
        e.preventDefault();
        return false;
    };

    $element.on('dragover dragleave', h);

    return Observable.using(
        () => new Subscription(() => $element.off('dragover dragleave', h)),
        () => Observable.fromEvent<Event>($element, 'drop')
            .do(e => { e.stopPropagation(); e.preventDefault(); })
            .map(e => (<any>e).originalEvent)
            .filter(e => e.dataTransfer.files && e.dataTransfer.files.length !== 0)
    );
}

//export function applyIE8SizeAndCenteringHackIfNecessary($element: JQuery, $input: JQuery, jiggeryPokeryBefore: () => void = null, jiggeryPokeryAfter: () => void = null, totalBorderThickness: number = 2) {
//    if ((!$input.is('input')) || (navigator.userAgent.indexOf('MSIE 8.0') === -1 && navigator.userAgent.indexOf('MSIE 7.0') === -1))
//        return Rx.Disposable.empty;

//    return Observable.just(true).merge(from($element, 'resize').map(() => true)).subscribe(() => {
//        if (jiggeryPokeryBefore)
//            jiggeryPokeryBefore();

//        var containerHeight = $element.height() - totalBorderThickness;
//        var top = Math.floor(containerHeight / 2);
//        var bottom = containerHeight - top;

//        $input.addClass('ie8heightcrossfix').css('padding-top', top + 'px').css('padding-bottom', bottom + 'px');

//        if (jiggeryPokeryAfter)
//            jiggeryPokeryAfter();
//    });
//}

export function scrollOutside($element) {
    return Observable.fromEventPattern(
        h => document.addEventListener('scroll', <any>h, true),
        h => document.removeEventListener('scroll', <any>h)
    ).map(x => (<any>x).target).filter(x => !$element.is(x) && !($element.has(x).length > 0));
}

export function restrictInput<T>(
    $element: JQuery,
    values: BehaviorSubject<T>,
    updateTrigger: Observable<any>,
    validator: (x: string) => boolean,
    converter: (x: string) => T,
    deconverter: (T) => string,
    onChanged?: (x: T) => void,
    equalityComparer?: (x, y) => boolean
): Subscription {
    const [valid, invalid] = updateTrigger.map(x => $element.val()).partition(x => validator(x));

    return compositeSubscription(
        values.subscribe(x => $element.val(deconverter(x))),
        invalid.withLatestFrom(values, (x, y) => y).subscribe(x => $element.val(deconverter(x))),
        valid.map(x => converter(x))
            .withLatestFrom(values, (x, y) => ({ oldValue: y, newValue: x }))
            .filter(x => equalityComparer ? !equalityComparer(x.newValue, x.oldValue) : x.newValue !== x.oldValue)
            .subscribe(x => {
                values.next(x.newValue);
                if (onChanged)
                    onChanged(x.newValue);
            }),
        // look, this isn't a stellar piece of work, but it addresses a lot of edge cases
        // perhaps this really is the simplest / best solution
        updateTrigger.withLatestFrom(values, (x, y) => y).subscribe(x => $element.val(deconverter(x)))
    );
}

export function enterPressed($element: JQuery): Observable<{}> {
    return Observable.fromEvent<KeyboardEvent>($element, 'keydown')
        .filter(x => (<any>x).keyCode === 13)
        .filter(x => !eventOriginWasAButton(x))
        .map(() => ({}));
}

export function spacePressed($element: JQuery): Observable<{}> {
    return Observable.fromEvent<KeyboardEvent>($element, 'keydown')
        .filter(x => (<any>x).keyCode === 32)
        .filter(x => !eventOriginWasAButton(x))
        .map(() => ({}));
}

export function escapePressed($element: JQuery): Observable<{}> {
    return Observable.fromEvent<KeyboardEvent>($element, 'keydown')
        .filter(x => (<any>x).keyCode === 27)
        .filter(x => !eventOriginWasAButton(x))
        .map(() => ({}));
}

function eventOriginWasAButton(x: KeyboardEvent) {
    return (x.target instanceof HTMLInputElement && (<HTMLInputElement>x.target).type === 'button');
}

export const wheelResolution = 50;

export function wheelEvent($element: JQuery) {
    return Observable.fromEvent<JQueryMousewheel.JQueryMousewheelEventObject>($element, 'wheel').map(x => <WheelEvent>x.originalEvent);
}

export function wheel($element: JQuery) {
    return wheelEvent($element).map(x => x.deltaY).map(x => x / Math.abs(x) * wheelResolution);
}